import { useFocusEffect, useRouter } from 'expo-router';
import {
  ArrowRight,
  CheckSquare,
  Columns3,
  Download,
  Filter,
  MoreHorizontal,
  Phone,
  Plus,
  Upload,
  Users,
  X,
} from 'lucide-react-native';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FlatList,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { FadeIn, sectionEntrance } from '../../../src/components/animations';
import { Box, HStack, Text, VStack } from '../../../src/components/primitives';
import { OverviewBadge } from '../../../src/components/primitives/BadgePresets';
import { Button } from '../../../src/components/primitives/Button';
import { EmptyState } from '../../../src/components/primitives/EmptyState';
import { ProgressBar } from '../../../src/components/primitives/ProgressBar';
import { SearchInput } from '../../../src/components/primitives/SearchInput';
import { StatusBanner } from '../../../src/components/primitives/StatusBanner';
import { TAB_ROUTE_BY_HREF } from '../../../src/config/navigation';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';
import { useTranslation } from '../../../src/contexts/TranslationContext';
import { LeadImportContainer } from '../../../src/features/lead-import';
import { BoardViewToggle } from '../../../src/features/leads';
import { LeadFilters } from '../../../src/features/leads/components/LeadFilters';
import { LeadListItem } from '../../../src/features/leads/components/LeadListItem';
import { LeadListSkeleton } from '../../../src/features/leads/components/LeadSkeletons';
import { StageMoveModal } from '../../../src/features/leads/components/StageMoveModal';
import { BatchCallAgentModal } from '../../../src/features/voice-agents/components/BatchCallAgentModal';
import {
  useCancelVoiceCallBatch,
  useTriggerBatchVoiceCall,
  useVoiceCallBatch,
} from '../../../src/features/voice-agents/hooks';
import { useCurrentOrgRole } from '../../../src/hooks/useAuthOrgRole';
import { useBoardRealtime } from '../../../src/hooks/useBoardRealtime';
import { useFunnelTemplates, useLeads, useMoveStage } from '../../../src/hooks/useLeads';
import { useOrgSettings } from '../../../src/hooks/useOrgSettings';
import { useResetOnKeyChange } from '../../../src/hooks/useResetOnKeyChange';
import { useTabNavResetKey } from '../../../src/hooks/useTabNavResetKey';
import { useTranslatedStageName } from '../../../src/hooks/useTranslatedStageName';
import { colors } from '../../../src/theme/colors';
import { tokens } from '../../../src/theme/tokens';
import type { BoardVisibilityInfo, Lead } from '../../../src/types/leads';

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    timerRef.current = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timerRef.current);
  }, [value, delay]);

  return debounced;
}

function LeadListSeparator() {
  // Transparent spacer: each LeadListItem now renders its own border so the
  // old 1px line separator was producing double-borders and visually fusing
  // the rows together. A taller invisible gap reads as proper cards.
  return <View style={{ height: 8 }} />;
}

// Pad the dataset so the last row has exactly `numColumns` cells. Without
// this, the trailing real card stretches to fill the row because each grid
// cell uses flex:1.
function padForGrid(leads: Lead[], numColumns: number): LeadOrPlaceholder[] {
  if (numColumns <= 1) return leads;
  const remainder = leads.length % numColumns;
  if (remainder === 0) return leads;
  const fillers: LeadOrPlaceholder[] = Array.from({ length: numColumns - remainder }, (_, i) => ({
    id: `__leads_placeholder_${i}`,
    __placeholder: true,
  }));
  return [...leads, ...fillers];
}

type LeadOrPlaceholder = Lead | { id: string; __placeholder: true };

function BatchCallProgressBanner({
  total,
  completedCount,
  failedCount,
  skippedCreditsCount,
  onCancel,
  onDismiss,
  cancelling,
  terminal,
}: Readonly<{
  total: number;
  completedCount: number;
  failedCount: number;
  skippedCreditsCount: number;
  onCancel: () => void;
  onDismiss: () => void;
  cancelling: boolean;
  terminal: boolean;
}>) {
  const { t } = useTranslation();
  const done = completedCount + failedCount;
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;
  const progress = t('voiceAgents.batchCall.progressLabel', { completed: done, total });
  const outOfCredits = skippedCreditsCount > 0;
  const message = outOfCredits
    ? `${progress} · ${t('voiceAgents.batchCall.creditsSkippedLabel', { count: skippedCreditsCount })}`
    : progress;

  return (
    <Box style={batchBannerStyles.wrapper}>
      <StatusBanner
        variant={outOfCredits ? 'warning' : 'info'}
        message={message}
        trailing={
          <HStack spacing="sm" align="center">
            {!terminal && (
              <Pressable
                testID="leads_batch_call_cancel_button"
                onPress={onCancel}
                disabled={cancelling}
                accessibilityRole="button"
                accessibilityLabel={t('common.actions.cancel')}
              >
                <Text variant="caption" color={colors.text.secondary}>
                  {t('common.actions.cancel')}
                </Text>
              </Pressable>
            )}
            {terminal && (
              <Pressable
                testID="leads_batch_call_dismiss_button"
                onPress={onDismiss}
                accessibilityRole="button"
                accessibilityLabel={t('common.actions.close')}
              >
                <X size={16} color={colors.text.tertiary} strokeWidth={2} />
              </Pressable>
            )}
          </HStack>
        }
      />
      <ProgressBar value={percent} />
    </Box>
  );
}

const COL_BREAKPOINT_3 = 1280;
const COL_BREAKPOINT_2 = 780;
const TOOLBAR_COMPACT_BREAKPOINT = 1080;

export default function LeadsListScreen() {
  const router = useRouter();
  const { currentOrganization, backendUser, getAccessToken } = useAuth();
  const { t } = useTranslation();
  const { showError } = useToast();
  const translateStageName = useTranslatedStageName();
  const { width: viewportWidth } = useWindowDimensions();

  // Land the list at the top whenever the user arrives at it (FLOSEAI-416).
  // Two paths must reset it, and on RN-web scrollToOffset misses the real
  // scroll node — so we remount the FlatList via its key instead:
  //  - sidebar / tab-bar navigation (re-tap, or jump from another section)
  //    emits a nav pulse → useTabNavResetKey.
  //  - going BACK from a lead detail is a router.back (no pulse), so we also
  //    bump on focus. The first focus is the initial mount (already at top),
  //    so we skip it to avoid a redundant remount on load.
  const pulseResetKey = useTabNavResetKey(TAB_ROUTE_BY_HREF['/leads']);
  const [focusResetKey, setFocusResetKey] = useState(0);
  const firstFocusRef = useRef(true);
  useFocusEffect(
    useCallback(() => {
      if (firstFocusRef.current) {
        firstFocusRef.current = false;
        return;
      }
      setFocusResetKey((k) => k + 1);
    }, []),
  );
  const listResetKey = `${pulseResetKey}-${focusResetKey}`;

  // Responsive grid: 1 col on phones, 2 cols on tablets, 3 cols on desktop.
  // FlatList caches numColumns internally — when the value changes we must
  // remount via the `key` prop or the layout won't re-flow.
  const numColumns = useMemo(() => {
    if (viewportWidth >= COL_BREAKPOINT_3) return 3;
    if (viewportWidth >= COL_BREAKPOINT_2) return 2;
    return 1;
  }, [viewportWidth]);

  const orgId = currentOrganization?.id;

  useBoardRealtime({
    organizationId: orgId,
    enabled: !!orgId,
    getAccessToken,
  });

  const { data: templates } = useFunnelTemplates(orgId);
  const role = useCurrentOrgRole();
  const isPrivileged = role === 'owner' || role === 'admin';
  const { data: orgSettings } = useOrgSettings(orgId);
  const boardVisibility = orgSettings?.boardVisibility ?? 'shared';
  const [viewMode, setViewMode] = useState<'my' | 'team'>('my');
  const activeTemplate = templates?.find((tpl) => tpl.isActive);

  const boardVisibilityInfo: BoardVisibilityInfo = useMemo(() => {
    const canViewTeam =
      isPrivileged || boardVisibility === 'shared' || boardVisibility === 'hybrid';
    const effectiveViewMode: 'my' | 'team' =
      boardVisibility === 'shared' ? 'team' : viewMode === 'team' ? 'team' : 'my';
    const isReadOnly =
      boardVisibility === 'hybrid' && !isPrivileged && effectiveViewMode === 'team';

    return {
      mode: boardVisibility,
      viewMode: effectiveViewMode,
      canViewTeam,
      isReadOnly,
    };
  }, [boardVisibility, isPrivileged, viewMode]);

  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [jobTitleFilter, setJobTitleFilter] = useState('');
  const [industryFilter, setIndustryFilter] = useState('');
  const [economicCapacityFilter, setEconomicCapacityFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const [showBulkStageModal, setShowBulkStageModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const [showBatchCallModal, setShowBatchCallModal] = useState(false);
  const [activeBatchId, setActiveBatchId] = useState<string | null>(null);

  // At compact widths (mobile + tablet portrait) we collapse the less-used
  // toolbar buttons behind an overflow menu so the primary actions never get
  // clipped past the viewport edge.
  const isCompactToolbar = viewportWidth < TOOLBAR_COMPACT_BREAKPOINT;

  const debouncedSearch = useDebouncedValue(search, 300);
  const debouncedJobTitle = useDebouncedValue(jobTitleFilter, 300);
  const debouncedIndustry = useDebouncedValue(industryFilter, 300);
  const debouncedLocation = useDebouncedValue(locationFilter, 300);

  const handleSortChange = useCallback((newSortBy: string, newSortOrder: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSourceFilter('');
    setStageFilter('');
    setSortBy('createdAt');
    setSortOrder('desc');
    setStartDate('');
    setEndDate('');
    setSearch('');
    setJobTitleFilter('');
    setIndustryFilter('');
    setEconomicCapacityFilter('');
    setLocationFilter('');
    setTagFilter([]);
  }, []);

  // FLOSEAI-534: re-entering Leads from another section (tab / sidebar) must
  // land on an unfiltered list. The pulse fires on chrome navigation only, so a
  // lead-detail round-trip (router.back) keeps the active filters.
  useResetOnKeyChange(pulseResetKey, handleClearFilters);

  const queryParams = useMemo(() => {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    return {
      organizationId: orgId,
      search: debouncedSearch || undefined,
      source: sourceFilter || undefined,
      stageId: stageFilter || undefined,
      jobTitle: debouncedJobTitle || undefined,
      industry: debouncedIndustry || undefined,
      economicCapacity: (economicCapacityFilter as 'High' | 'Medium' | 'Low') || undefined,
      location: debouncedLocation || undefined,
      tags: tagFilter.length > 0 ? tagFilter : undefined,
      sortBy: sortBy as 'createdAt' | 'updatedAt' | 'fullName' | 'company',
      sortOrder,
      startDate: dateRegex.test(startDate) ? startDate : undefined,
      endDate: dateRegex.test(endDate) ? endDate : undefined,
      limit: 100,
      viewMode: isPrivileged ? undefined : viewMode,
    };
  }, [
    orgId,
    debouncedSearch,
    sourceFilter,
    stageFilter,
    debouncedJobTitle,
    debouncedIndustry,
    economicCapacityFilter,
    debouncedLocation,
    tagFilter,
    sortBy,
    sortOrder,
    startDate,
    endDate,
    isPrivileged,
    viewMode,
  ]);

  const { data, isLoading } = useLeads(queryParams, !!orgId);
  const moveStage = useMoveStage();
  const triggerBatchCall = useTriggerBatchVoiceCall();
  const cancelBatchCall = useCancelVoiceCallBatch();
  const batchCallQuery = useVoiceCallBatch(activeBatchId ?? '', orgId ?? '');

  const filtersActive = !!(
    search ||
    sourceFilter ||
    stageFilter ||
    jobTitleFilter ||
    industryFilter ||
    economicCapacityFilter ||
    locationFilter ||
    tagFilter.length > 0 ||
    startDate ||
    endDate ||
    sortBy !== 'createdAt' ||
    sortOrder !== 'desc'
  );

  const handleLeadPress = useCallback(
    (lead: Lead) => {
      if (bulkMode) {
        setSelectedLeads((prev) => {
          const next = new Set(prev);
          if (next.has(lead.id)) {
            next.delete(lead.id);
          } else {
            next.add(lead.id);
          }
          return next;
        });
      } else if (
        backendUser &&
        boardVisibilityInfo.isReadOnly &&
        lead.assignedTo &&
        lead.assignedTo !== backendUser.id
      ) {
        showError(t('leads.viewMode.readOnly'));
      } else {
        router.push(`/lead/${lead.id}`);
      }
    },
    [bulkMode, router.push, boardVisibilityInfo.isReadOnly, backendUser, showError, t],
  );

  const handleSelectLead = useCallback((lead: Lead) => {
    setSelectedLeads((prev) => {
      const next = new Set(prev);
      if (next.has(lead.id)) {
        next.delete(lead.id);
      } else {
        next.add(lead.id);
      }
      return next;
    });
  }, []);

  const handleBulkMoveStage = useCallback(
    async (stageId: string) => {
      const promises = Array.from(selectedLeads).map((leadId) =>
        moveStage.mutateAsync({ leadId, stageId }),
      );
      await Promise.all(promises);
      setSelectedLeads(new Set());
      setBulkMode(false);
      setShowBulkStageModal(false);
    },
    [selectedLeads, moveStage.mutateAsync],
  );

  const handleBatchCall = useCallback(
    (voiceAgentId: string) => {
      if (!orgId) return;
      triggerBatchCall.mutate(
        { organizationId: orgId, voiceAgentId, leadIds: Array.from(selectedLeads) },
        {
          onSuccess: (batch) => {
            setActiveBatchId(batch.id);
            setShowBatchCallModal(false);
            setSelectedLeads(new Set());
            setBulkMode(false);
          },
        },
      );
    },
    [orgId, selectedLeads, triggerBatchCall],
  );

  // The progress banner PERSISTS after the batch reaches a terminal state so
  // the user can actually read the final "N of N" result — it clears only on
  // explicit dismiss (leads_batch_call_dismiss_button). Polling already stops
  // once the batch is terminal (useVoiceCallBatch.refetchInterval).

  const handleExportCSV = useCallback(() => {
    if (!data?.leads) return;

    const leadsToExport = bulkMode ? data.leads.filter((l) => selectedLeads.has(l.id)) : data.leads;

    const escapeField = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;

    const headers = [
      'Name',
      'Email',
      'Phone',
      'Company',
      'Job Title',
      'Source',
      'Stage',
      'Assigned To',
      'Tags',
      'Score',
      'Industry',
      'Location',
      'Created',
      'Updated',
    ];

    const rows = leadsToExport.map((l) => {
      const cf = (l.customFields || {}) as Record<string, unknown>;
      return [
        escapeField(l.fullName || ''),
        escapeField(l.email || ''),
        escapeField(l.phone || ''),
        escapeField(l.company || ''),
        escapeField(l.jobTitle || ''),
        escapeField(l.source || ''),
        escapeField(l.currentStage?.name || ''),
        escapeField(l.assignedUser?.fullName || ''),
        escapeField((l.tags || []).join('; ')),
        escapeField(cf.score != null ? String(cf.score) : ''),
        escapeField((cf.industry as string) || ''),
        escapeField((cf.location as string) || ''),
        escapeField(l.createdAt ? new Date(l.createdAt).toLocaleDateString() : ''),
        escapeField(l.updatedAt ? new Date(l.updatedAt).toLocaleDateString() : ''),
      ].join(',');
    });

    const bom = '\uFEFF';
    const csv = bom + [headers.join(','), ...rows].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [data, bulkMode, selectedLeads]);

  const stages = activeTemplate?.stages || [];

  const tagOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const tag of tagFilter) seen.add(tag);
    for (const lead of data?.leads ?? []) {
      for (const tag of lead.tags ?? []) seen.add(tag);
    }
    return Array.from(seen)
      .sort((a, b) => a.localeCompare(b))
      .map((tag) => ({ value: tag, label: tag }));
  }, [data?.leads, tagFilter]);

  const stageOptions = [
    { value: '', label: t('leads.list.allStages') },
    ...stages.map((stage) => ({
      value: stage.id,
      label: translateStageName(stage.name),
      color: stage.color,
    })),
  ];

  return (
    <View testID="leads_screen" style={layoutStyles.root}>
      <View style={layoutStyles.content}>
        <FadeIn {...sectionEntrance(0)} style={layoutStyles.sectionSpacing}>
          <HStack justify="between" align="center" wrap style={layoutStyles.toolbarRow}>
            <VStack spacing="xs" style={layoutStyles.toolbarTitle}>
              <Text variant="h4" color={colors.text.primary} style={layoutStyles.heading}>
                {t('leads.list.title')}
              </Text>
              <Text variant="caption" color={colors.text.tertiary}>
                {data?.total ?? 0}{' '}
                {(data?.total ?? 0) === 1 ? t('leads.list.totalLead') : t('leads.list.totalLeads')}
              </Text>
            </VStack>

            <HStack spacing="sm" align="center" style={layoutStyles.toolbarActions} wrap>
              {isPrivileged && (
                <OverviewBadge size="sm" style={{ alignSelf: 'center' }}>
                  {t(`settings.boardVisibility.${boardVisibility}`)}
                </OverviewBadge>
              )}
              {!isPrivileged && boardVisibilityInfo.isReadOnly && (
                <Box style={layoutStyles.readOnlyBadge}>
                  <Text variant="caption" color={colors.status.warning.main}>
                    {t('leads.viewMode.readOnly')}
                  </Text>
                </Box>
              )}
              {!isPrivileged && (
                <BoardViewToggle visibility={boardVisibilityInfo} onViewModeChange={setViewMode} />
              )}
              <Button
                testID="leads_bulk_mode_button"
                variant={bulkMode ? 'secondary' : 'ghost'}
                size="sm"
                onPress={() => {
                  setBulkMode(!bulkMode);
                  setSelectedLeads(new Set());
                }}
              >
                <CheckSquare
                  size={16}
                  color={bulkMode ? colors.primary[500] : colors.text.secondary}
                  strokeWidth={2}
                />
              </Button>
              <Button
                testID="leads_kanban_button"
                variant="ghost"
                size="sm"
                onPress={() => router.push('/funnel')}
              >
                <Columns3 size={16} color={colors.text.secondary} strokeWidth={2} />
              </Button>
              {!isCompactToolbar && Platform.OS === 'web' && (
                <Button
                  testID="leads_export_csv_button"
                  variant="ghost"
                  size="sm"
                  onPress={handleExportCSV}
                >
                  <Download size={16} color={colors.text.secondary} strokeWidth={2} />
                </Button>
              )}
              {!isCompactToolbar && (
                <Button
                  testID="leads_import_button"
                  variant="outline"
                  size="sm"
                  onPress={() => setShowImportModal(true)}
                >
                  <Upload size={16} color={colors.primary[500]} strokeWidth={2.5} />
                </Button>
              )}
              {isCompactToolbar &&
                (() => {
                  // Popover content is rendered the same way on web (inline,
                  // anchored under the trigger) and native (inside a Modal,
                  // positioned near the top-right so it sits roughly where
                  // the More button is). Extracted so the JSX doesn't fork.
                  const popoverItems = (
                    <>
                      {Platform.OS === 'web' && (
                        <Pressable
                          testID="leads_more_export_button"
                          onPress={() => {
                            handleExportCSV();
                            setShowMoreActions(false);
                          }}
                          style={({ pressed }) => [
                            layoutStyles.morePopoverItem,
                            pressed && layoutStyles.morePopoverItemPressed,
                          ]}
                        >
                          <Download size={16} color={colors.text.secondary} strokeWidth={2} />
                          <Text variant="bodySmall" color={colors.text.primary}>
                            {t('common.actions.export')}
                          </Text>
                        </Pressable>
                      )}
                      <Pressable
                        testID="leads_more_import_button"
                        onPress={() => {
                          setShowImportModal(true);
                          setShowMoreActions(false);
                        }}
                        style={({ pressed }) => [
                          layoutStyles.morePopoverItem,
                          pressed && layoutStyles.morePopoverItemPressed,
                        ]}
                      >
                        <Upload size={16} color={colors.primary[500]} strokeWidth={2.5} />
                        <Text variant="bodySmall" color={colors.text.primary}>
                          {t('common.actions.import')}
                        </Text>
                      </Pressable>
                    </>
                  );

                  return (
                    <View style={layoutStyles.moreWrapper}>
                      <Button
                        testID="leads_more_actions_button"
                        variant="ghost"
                        size="sm"
                        onPress={() => setShowMoreActions((v) => !v)}
                      >
                        <MoreHorizontal size={16} color={colors.text.secondary} strokeWidth={2} />
                      </Button>

                      {/* Web: keep the inline absolute-positioned backdrop +
                          popover anchored under the trigger. Web's overflow
                          stacking context makes the negative-offset Pressable
                          reliably catch outside clicks. */}
                      {Platform.OS === 'web' && showMoreActions && (
                        <>
                          <Pressable
                            style={layoutStyles.moreBackdrop}
                            onPress={() => setShowMoreActions(false)}
                          />
                          <View style={layoutStyles.morePopover}>{popoverItems}</View>
                        </>
                      )}

                      {/* Native: render in a Modal so the backdrop covers the
                          whole screen and catches taps over the FlatList
                          rows. The inline `position:absolute` backdrop in
                          the previous implementation didn't reliably
                          intercept those taps because RN's Yoga doesn't
                          stack the toolbar's overflow above sibling
                          content the way web does. */}
                      {Platform.OS !== 'web' && (
                        <Modal
                          transparent
                          visible={showMoreActions}
                          animationType="fade"
                          onRequestClose={() => setShowMoreActions(false)}
                        >
                          <Pressable
                            style={StyleSheet.absoluteFill}
                            onPress={() => setShowMoreActions(false)}
                          />
                          <View style={layoutStyles.morePopoverNative}>{popoverItems}</View>
                        </Modal>
                      )}
                    </View>
                  );
                })()}
              <Button
                testID="leads_add_button"
                variant="primary"
                size="sm"
                onPress={() => router.push('/lead/new')}
              >
                <Plus size={18} color={colors.background.primary} strokeWidth={2.5} />
              </Button>
            </HStack>
          </HStack>
        </FadeIn>

        <FadeIn {...sectionEntrance(1)} style={layoutStyles.sectionSpacing}>
          <VStack spacing="sm">
            <HStack spacing="sm">
              <View style={layoutStyles.searchInput}>
                <SearchInput
                  value={search}
                  onChangeText={setSearch}
                  placeholder={t('leads.list.searchPlaceholder')}
                  testID="leads-search"
                />
              </View>
              <Button
                testID="leads_filter_button"
                variant={showFilters ? 'secondary' : 'outline'}
                size="sm"
                onPress={() => setShowFilters(!showFilters)}
              >
                <Filter size={16} color={colors.text.secondary} strokeWidth={2} />
              </Button>
            </HStack>

            {showFilters && (
              <LeadFilters
                sourceFilter={sourceFilter}
                onSourceChange={setSourceFilter}
                stageFilter={stageFilter}
                onStageChange={setStageFilter}
                stageOptions={stageOptions}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={handleSortChange}
                startDate={startDate}
                onStartDateChange={setStartDate}
                endDate={endDate}
                onEndDateChange={setEndDate}
                jobTitleFilter={jobTitleFilter}
                onJobTitleChange={setJobTitleFilter}
                industryFilter={industryFilter}
                onIndustryChange={setIndustryFilter}
                economicCapacityFilter={economicCapacityFilter}
                onEconomicCapacityChange={setEconomicCapacityFilter}
                locationFilter={locationFilter}
                onLocationChange={setLocationFilter}
                tagFilter={tagFilter}
                onTagChange={setTagFilter}
                tagOptions={tagOptions}
                onClearFilters={handleClearFilters}
                hasActiveFilters={filtersActive}
              />
            )}
          </VStack>
        </FadeIn>

        <Box flex={1} testID={isLoading ? undefined : 'leads_results'}>
          {isLoading && <LeadListSkeleton />}
          {!isLoading && !data?.leads?.length && (
            <Box flex={1} alignItems="center" justifyContent="center" padding="xxl">
              <EmptyState
                icon={<Users size={32} color={colors.text.tertiary} strokeWidth={1.5} />}
                title={filtersActive ? t('leads.list.noMatchFilters') : t('leads.list.noLeadsYet')}
                description={
                  filtersActive
                    ? t('leads.list.noMatchDescription')
                    : t('leads.list.noLeadsDescription')
                }
                action={
                  filtersActive
                    ? undefined
                    : {
                        label: t('common.actions.addLead'),
                        onPress: () => router.push('/lead/new'),
                      }
                }
              />
            </Box>
          )}
          {!isLoading && !!data?.leads?.length && (
            <FlatList<LeadOrPlaceholder>
              testID="leads_list"
              // Remount on column change (FlatList doesn't re-render numColumns
              // dynamically) and on chrome navigation to reset scroll to top.
              key={`leads-cols-${numColumns}-${listResetKey}`}
              data={padForGrid(data.leads, numColumns)}
              keyExtractor={(item) => item.id}
              numColumns={numColumns}
              columnWrapperStyle={numColumns > 1 ? layoutStyles.columnWrapper : undefined}
              renderItem={({ item }) => {
                if ('__placeholder' in item) {
                  return <View style={layoutStyles.gridItem} />;
                }
                const card = (
                  <LeadListItem
                    lead={item}
                    onPress={handleLeadPress}
                    selected={selectedLeads.has(item.id)}
                    onSelect={handleSelectLead}
                    showCheckbox={bulkMode}
                    numColumns={numColumns}
                  />
                );
                return numColumns > 1 ? <View style={layoutStyles.gridItem}>{card}</View> : card;
              }}
              ItemSeparatorComponent={numColumns === 1 ? LeadListSeparator : undefined}
              contentContainerStyle={layoutStyles.listContent}
            />
          )}
        </Box>
      </View>

      {activeBatchId && batchCallQuery.data && (
        <BatchCallProgressBanner
          total={batchCallQuery.data.total}
          completedCount={batchCallQuery.data.completedCount}
          failedCount={batchCallQuery.data.failedCount}
          skippedCreditsCount={batchCallQuery.data.skippedCreditsCount}
          terminal={batchCallQuery.data.status !== 'running'}
          cancelling={cancelBatchCall.isPending}
          onCancel={() => {
            if (!orgId) return;
            cancelBatchCall.mutate({ id: activeBatchId, organizationId: orgId });
          }}
          onDismiss={() => setActiveBatchId(null)}
        />
      )}

      {bulkMode && selectedLeads.size > 0 && (
        <Box style={bulkBarStyles.wrapper}>
          <Box style={bulkBarStyles.bar}>
            <Text variant="caption" color={colors.text.secondary} style={bulkBarStyles.countLabel}>
              {selectedLeads.size} {t('leads.list.selected')}
            </Text>

            <Pressable
              testID="leads_bulk_move_button"
              onPress={() => setShowBulkStageModal(true)}
              style={({ pressed }) => [
                bulkBarStyles.action,
                bulkBarStyles.moveAction,
                pressed && bulkBarStyles.actionPressed,
              ]}
              accessibilityRole="button"
              accessibilityLabel={`Move ${selectedLeads.size} leads`}
            >
              <ArrowRight size={14} color={colors.background.primary} strokeWidth={2} />
              <Text
                variant="caption"
                color={colors.background.primary}
                style={bulkBarStyles.actionLabel}
              >
                {t('common.actions.move')} ({selectedLeads.size})
              </Text>
            </Pressable>

            <Pressable
              testID="leads_bulk_call_button"
              disabled={!!activeBatchId}
              onPress={() => {
                // Don't clobber an active batch's banner (running or its unread
                // terminal result) with a new one — dismiss/finish it first.
                if (!activeBatchId) setShowBatchCallModal(true);
              }}
              style={({ pressed }) => [
                bulkBarStyles.action,
                bulkBarStyles.callAction,
                pressed && bulkBarStyles.actionPressed,
                !!activeBatchId && { opacity: 0.5 },
              ]}
              accessibilityRole="button"
              accessibilityState={{ disabled: !!activeBatchId }}
              accessibilityLabel={`Call ${selectedLeads.size} leads`}
            >
              <Phone size={14} color={colors.background.primary} strokeWidth={2} />
              <Text
                variant="caption"
                color={colors.background.primary}
                style={bulkBarStyles.actionLabel}
              >
                {t('common.actions.call')} ({selectedLeads.size})
              </Text>
            </Pressable>

            {Platform.OS === 'web' && (
              <Pressable
                testID="leads_export_button"
                onPress={handleExportCSV}
                style={({ pressed }) => [
                  bulkBarStyles.action,
                  bulkBarStyles.exportAction,
                  pressed && bulkBarStyles.actionPressed,
                ]}
                accessibilityRole="button"
                accessibilityLabel="Export selected leads"
              >
                <Download size={14} color={colors.text.primary} strokeWidth={2} />
                <Text
                  variant="caption"
                  color={colors.text.primary}
                  style={bulkBarStyles.actionLabel}
                >
                  {t('common.actions.export')}
                </Text>
              </Pressable>
            )}
          </Box>
        </Box>
      )}
      {showBulkStageModal && stages.length > 0 && (
        <StageMoveModal
          visible={showBulkStageModal}
          onClose={() => setShowBulkStageModal(false)}
          stages={stages}
          currentStageId=""
          onSelectStage={handleBulkMoveStage}
          loading={moveStage.isPending}
        />
      )}

      {showBatchCallModal && (
        <BatchCallAgentModal
          visible
          organizationId={orgId ?? ''}
          leadCount={selectedLeads.size}
          onConfirm={handleBatchCall}
          onClose={() => setShowBatchCallModal(false)}
        />
      )}

      <LeadImportContainer visible={showImportModal} onClose={() => setShowImportModal(false)} />
    </View>
  );
}

const layoutStyles = StyleSheet.create({
  root: {
    flex: 1,
    position: 'relative',
    // overflow + minWidth:0 are CSS patterns that solve web horizontal scroll
    // but on iOS they can collapse the flex content area and hide the list.
    ...(Platform.OS === 'web'
      ? { overflow: 'hidden' as const, width: '100%', maxWidth: '100%' }
      : {}),
  },
  content: {
    flex: 1,
    flexDirection: 'column',
    // Match the dashboard tab's content inset so the tabs align visually.
    paddingHorizontal: tokens.spacing.lg,
    paddingTop: tokens.spacing.lg,
    ...(Platform.OS === 'web' ? { minWidth: 0, width: '100%', maxWidth: '100%' } : {}),
  },
  toolbarRow: {
    // Allow the title + actions to wrap to a new line if the viewport is too
    // narrow. Without this, justify="between" + nowrap pushes the rightmost
    // action buttons past the viewport edge where they get clipped by the
    // root's overflow:hidden — invisible to the user.
    rowGap: tokens.spacing.sm,
    width: '100%',
  },
  toolbarTitle: {
    flexShrink: 1,
    minWidth: 0,
  },
  // flexShrink:0 keeps the action block at its natural width so the OUTER
  // wrapping HStack drops it to a second row when the title row can't fit
  // both. With flexShrink:1 the block would silently compress and push its
  // children past the viewport edge instead of wrapping.
  toolbarActions: {
    flexShrink: 0,
    flexWrap: 'wrap',
    rowGap: tokens.spacing.xs,
  },
  readOnlyBadge: {
    backgroundColor: colors.status.warning.bg,
    borderRadius: tokens.radius.sm,
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: 2,
    alignSelf: 'center',
  },
  moreWrapper: {
    position: 'relative',
  },
  moreBackdrop: {
    position: 'absolute',
    top: -1000,
    left: -1000,
    right: -1000,
    bottom: -1000,
    zIndex: tokens.zIndex?.sticky ?? 100,
  },
  morePopover: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: tokens.spacing.xs,
    minWidth: 160,
    backgroundColor: colors.surface.row,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.border.muted,
    paddingVertical: tokens.spacing.xs,
    zIndex: (tokens.zIndex?.sticky ?? 100) + 1,
    ...tokens.shadow?.lg,
  },
  // Native Modal-anchored variant of morePopover. Positioned at the top-right
  // of the screen because the More button sits inside the toolbar near the
  // top edge; an exact trigger-relative anchor would require measuring the
  // button's screen coords via onLayout. The approximate anchor reads as the
  // same affordance visually and the Modal backdrop reliably closes the menu
  // on any outside tap.
  morePopoverNative: {
    position: 'absolute',
    top: 96,
    right: tokens.spacing.lg,
    minWidth: 200,
    backgroundColor: colors.surface.row,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.border.muted,
    paddingVertical: tokens.spacing.xs,
    ...tokens.shadow?.lg,
  },
  morePopoverItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.sm,
  },
  morePopoverItemPressed: {
    backgroundColor: colors.background.elevated,
  },
  // Replaces VStack's gap; relying on Yoga gap with a flex:1 sibling collapsed
  // the list on iOS.
  sectionSpacing: {
    marginBottom: tokens.spacing.lg,
  },
  heading: {
    fontWeight: tokens.fontWeight.bold,
  },
  // Wrapping View around <SearchInput>. SearchInput's internal wrapper is
  // width:100% which on iOS Yoga (flex-shrink:0 default) grabs the full
  // HStack width and pushes the filter button off-screen. Hosting it inside
  // a flex:1 + minWidth:0 container makes the flex distribution authoritative.
  searchInput: {
    flex: 1,
    minWidth: 0,
  },
  listContent: {
    paddingVertical: 4,
  },
  // columnGap is honored by RN Web and modern RN; native pre-0.71 falls back
  // gracefully (cards still render, just without the inter-cell gap). The
  // marginBottom keeps vertical spacing between rows in the grid.
  columnWrapper: {
    marginBottom: tokens.spacing.md,
    columnGap: tokens.spacing.md,
  },
  // flex:1 + flexBasis:0 + minWidth:0 + overflow:hidden lets each cell take
  // exactly 1/numColumns of the row width and clip any wide intrinsic child.
  // maxWidth:'100%' is a defensive guard — without it, a long unbreakable
  // string inside the card can sometimes inflate the cell past its flex slot
  // on RN Web.
  gridItem: {
    flex: 1,
    flexBasis: 0,
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
  },
});

const batchBannerStyles = StyleSheet.create({
  wrapper: {
    marginBottom: tokens.spacing.md,
    gap: tokens.spacing.xs,
  },
});

const bulkBarStyles = StyleSheet.create({
  wrapper: {
    position: 'absolute',
    bottom: tokens.spacing.xl,
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: tokens.zIndex.sticky,
    pointerEvents: 'box-none',
  },
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md,
    backgroundColor: colors.surface.row,
    borderRadius: tokens.radius.xl,
    borderWidth: 1,
    borderColor: colors.border.muted,
    paddingHorizontal: tokens.spacing.xl,
    paddingVertical: tokens.spacing.md,
    ...tokens.shadow?.lg,
    ...Platform.select({
      web: {
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
      } as Record<string, string>,
      default: {},
    }),
  },
  countLabel: {
    fontWeight: tokens.fontWeight.medium,
    marginRight: tokens.spacing.sm,
  },
  action: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs,
    paddingHorizontal: tokens.spacing.lg,
    paddingVertical: tokens.spacing.sm,
    borderRadius: tokens.radius.md,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transitionProperty: 'opacity',
        transitionDuration: `${tokens.duration.fast}ms`,
        userSelect: 'none',
      } as Record<string, string>,
      default: {},
    }),
  },
  moveAction: {
    backgroundColor: colors.primary[500],
  },
  callAction: {
    backgroundColor: colors.status.success.main,
  },
  exportAction: {
    backgroundColor: colors.background.elevated,
    borderWidth: 1,
    borderColor: colors.border.muted,
  },
  actionPressed: {
    opacity: 0.8,
  },
  actionLabel: {
    fontWeight: tokens.fontWeight.medium,
  },
});
